import { useState, type ReactNode } from 'react';
import { DataTable, type Column } from './DataTable';
import { ConfirmDialog } from './Feedback';
import { PageHeader } from './PageHeader';
import { FilterBar, defaultFilters, type FilterDef } from './FilterBar';
import { IconRefresh, IconTrash } from './Icons';
import { useMutation, usePagedList } from '../lib/hooks';
import { api } from '../lib/api';

type Props<T> = {
  title: string;
  subtitle?: string;
  /** Endpoint returning PagedResult<T>. */
  endpoint: string;
  columns: Column<T>[];
  rowKey: (row: T) => string | number;
  /** Buttons rendered to the right of the heading. */
  actions?: ReactNode;
  /** Per-column filters. Rendered above the table; combined with AND on the server. */
  filters?: FilterDef[];
  emptyMessage?: string;
  /** Enables a Delete button per row when supplied. */
  remove?: {
    /** Endpoint for the DELETE, built from the row. */
    url: (row: T) => string;
    /** Text shown in the confirmation dialog. */
    describe: (row: T) => ReactNode;
    label?: string;
    successMessage?: string;
  };
  /** Extra per-row controls, rendered before the delete button. */
  rowActions?: (row: T, reload: () => Promise<void>) => ReactNode;
};

/**
 * The standard list screen: paged table, per-column filters, optional delete-with-confirm.
 *
 * There is deliberately no free-text search box. It only ever matched the rows already
 * loaded into the page, so a name sitting on page 3 looked like it did not exist - which is
 * precisely the confusion the per-column filters were added to end. Every filter now goes to
 * the server, so a match is found wherever it lives in the table.
 *
 * Written once so that pagination, page-size selection, toasts and destructive-action
 * confirmation behave identically on every screen, rather than depending on whoever built
 * each page remembering to add them.
 */
export function ListPage<T>({
  title,
  subtitle,
  endpoint,
  columns,
  rowKey,
  actions,
  filters,
  emptyMessage,
  remove,
  rowActions,
}: Props<T>) {
  // Computed once, on mount: usePagedList reads it as the initial state, so a screen with a
  // default filter narrows the very first request rather than flashing the full list.
  const list = usePagedList<T>(endpoint, {}, defaultFilters(filters ?? []));
  const { run, busy } = useMutation();
  const [pendingDelete, setPendingDelete] = useState<T | null>(null);

  const allColumns: Column<T>[] = [...columns];

  if (remove || rowActions) {
    allColumns.push({
      key: '__actions',
      header: '',
      align: 'right',
      width: '1%',
      render: (row) => (
        <span className="dt__rowactions">
          {rowActions?.(row, list.reload)}
          {remove && (
            <button
              type="button"
              className="btn btn--sm btn--danger-quiet"
              onClick={() => setPendingDelete(row)}
              title={remove.label ?? 'Delete'}
            >
              <IconTrash />
              {remove.label ?? 'Delete'}
            </button>
          )}
        </span>
      ),
    });
  }

  async function confirmDelete() {
    if (!remove || !pendingDelete) return;

    const ok = await run(
      () => api.delete(remove.url(pendingDelete)),
      remove.successMessage ?? 'Deleted.',
      () => list.reload(),
    );

    if (ok) setPendingDelete(null);
  }

  return (
    <>
      <PageHeader
        title={title}
        subtitle={subtitle}
        actions={
          <>
            {actions}
            <button
              type="button"
              className="btn btn--icon"
              onClick={() => void list.reload()}
              disabled={list.loading}
              title="Refresh"
              aria-label="Refresh"
            >
              <IconRefresh />
            </button>
          </>
        }
      />

      <DataTable
        columns={allColumns}
        data={list.data}
        loading={list.loading}
        error={list.error}
        rowKey={rowKey}
        emptyMessage={emptyMessage}
        onPageChange={list.setPage}
        onPageSizeChange={list.setPageSize}
        above={
          filters && filters.length > 0 ? (
            <FilterBar
              filters={filters}
              values={list.filters}
              onApply={list.setFilters}
              busy={list.loading}
            />
          ) : undefined
        }
      />

      {remove && (
        <ConfirmDialog
          open={pendingDelete !== null}
          destructive
          busy={busy}
          title="Are you sure?"
          confirmLabel={remove.label ?? 'Delete'}
          message={pendingDelete ? remove.describe(pendingDelete) : null}
          onConfirm={() => void confirmDelete()}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </>
  );
}
